import React from "react";
import { GroupListItem } from "@themuse/design-system/lib/components/GroupList/GroupList";
import { FiltersActionTypes } from "interfaces/context/searchContextInterfaces";
import { useSearchDispatch, useSearchState } from "hooks/SearchContext";
import { IconClose } from "../icons/IconClose";
import { FilterOption, FilterOptionProps } from "./FilterOption";
import styles from "./FilterPanel.module.scss";
import { IconLeftArrow } from "../icons/IconLeftArrow";

export interface FilterPanelProps {
    filterID: string;
    options: GroupListItem[];
    label: string;
    onCancel: () => void;
    onApply: () => void;
    isOpen?: boolean
    onBackBtn: () => void;
}

/**
 * `FilterPanel` component provides a UI for selecting individual filter options.
 */
export const FilterPanel: React.FC<FilterPanelProps> = ({
    filterID,
    isOpen = false,
    label,
    options,
    onCancel,
    onApply,
    onBackBtn
}) => {
    const searchDispatch = useSearchDispatch();
    const {
        filters
    } = useSearchState();
    // Radio groups take strings while all the others use string arrays.
    const isRadioGroup = ["postedDateRange", "radius", "allFiltersBtn"].includes(filterID);
    const isAllFilters = filterID === "allFiltersBtn" ? styles.allFilters : "";

    // Check if this value is selected.
    const isChecked = (value: string) => {
        try {
            return isRadioGroup ? filters[filterID] === value : filters[filterID].includes(value);
        } catch (error) {
            console.error(`Error checking isChecked Filter: ${filterID} : Item: ${value}`);
            return false;
        }
    };

    /**
     * Check if the currently selected items are different than the current search result filters.
     */
    const hasSelections = isRadioGroup ? !!filters[filterID] : !!filters[filterID].length;

    const updateCheckState = (value: string) => (isChecked(value)
        // I'ts already in the list, let's remove this item.
        ? filters[filterID].filter((item) => item !== value)
        // I'ts not in the list, let's add this item.
        : [...filters[filterID], value]);

    const updateRadioState = (value: string) => (isChecked(value) ? "" : value);

    /**
     * Handles the selection of an item by its value.
     * Toggles the item's presence in the selections list:
     * - If the item is already selected, it removes it.
     * - If the item is not selected, it adds it.
     */
    const onSelect = (value: string): void => {
        searchDispatch({
            type: FiltersActionTypes.updateFilter,
            payload: {
                [filterID]: isRadioGroup ? updateRadioState(value) : updateCheckState(value)
            }
        });
    };

    const onReset = (): void => {
        searchDispatch({
            type: FiltersActionTypes.updateFilter,
            payload: {
                [filterID]: isRadioGroup ? "" : []
            }
        });
    };

    /**
     * Handles the cancellation of the current operation by invoking the onCancel callback
     * and resetting the selections to an empty array.
     */
    const onCancelClose = () => {
        onCancel();
    };

    const onSubmit = (): void => {
        // currently this only closes the panel. this is because the checking of an item preforms a filter.
        // this button is only for show.
        onApply();
    };

    return (
        <div
            className={`${styles.container} ${isOpen ? styles.open : ""} ${isAllFilters}`}
            role="dialog"
            aria-label="Filter options"
        >
            <div className={styles.FilterPanelMenu}>
                <button
                    type="button"
                    className={styles.closeButton}
                    onClick={onCancelClose}
                    aria-label="Close"
                >
                    <IconClose />
                </button>

                <div className={styles.menuHeader}>
                    <button
                        type="button"
                        onClick={onBackBtn}
                        aria-label="Back to all filters"
                        className={styles.backButton}
                    >
                        <IconLeftArrow />
                        All Filters
                    </button>
                    <p className={styles.panelTitle}>{label}</p>
                </div>

                <div className={styles.filterContent}>
                    <div
                        className={styles.optionsContainer}
                        role={isRadioGroup ? "radiogroup" : "group"}
                    >
                        {options.map((option: FilterOptionProps) => (
                            <FilterOption
                                key={option.value}
                                value={option.value}
                                label={option.label}
                                isSelected={isChecked(option.value)}
                                isRadioGroup={isRadioGroup}
                                onSelect={() => onSelect(option.value)}
                            />
                        ))}
                    </div>
                </div>

                <div className={styles.actionButtons}>
                    <button
                        type="button"
                        className={styles.cancelButton}
                        onClick={hasSelections ? onReset : onCancelClose}
                    >
                        {hasSelections ? "Reset" : "Cancel"}
                    </button>
                    <button
                        type="button"
                        className={styles.applyButton}
                        onClick={onSubmit}
                    >
                        Apply
                    </button>
                </div>
            </div>
        </div>
    );
};
